// @flow

import React, { Component } from "react"
import { connect } from "react-redux"
import style from "./style.css"
import BigList from "../../components/BigList"
import Card from "../../components/Card"
import Spacer from "../../components/Spacer"
import LabeledValue from "../../components/LabeledValue"
import { getAllInterestTypes, updateInterestType } from "../../actions"
import { withRouter } from "react-router-dom"
import { formatPercent, formatDate, getCurrentInterestTax } from "../../utils"
import Input from "react-toolbox/lib/input"
import Dialog from "react-toolbox/lib/dialog"
import Division from "../../components/Division"
import FontIcon from "react-toolbox/lib/font_icon/FontIcon"
import DatePicker from "../../components/DatePicker"
import { toastr } from "react-redux-toastr"
import _ from "lodash"
import * as strings from "../../strings"
import {
  ResponsiveContainer, LineChart, XAxis,
  YAxis, CartesianGrid, Line,
  Tooltip,
} from "recharts"

const INTEREST_TYPE_COLUMNS = [ {
  title: strings.generalStrings.name,
  key: "name",
}, {
  title: strings.generalStrings.currentValue,
  key: "currentValue",
}, {
  title: strings.generalStrings.startDate,
  key: "startDate",
}, {
  title: strings.generalStrings.endDate,
  key: "endDate",
} ]

/** ****************************************************************************
*                                  Constants
*******************************************************************************/

const ONEDAY = 1

/** ****************************************************************************
*                                  Component
*******************************************************************************/

class ManageInterestType extends Component {
  props: {
    history: any,
    match: {
      path: string,
      params: {
        id?: string,
      },
    },
    interestTypes: mixed[],
    getAllInterestTypes: () => any,
    updateInterestType: ( mixed ) => any,
  }

  componentWillMount () {
    if ( this.props.interestTypes.length === 0 ) {
      this.props.getAllInterestTypes()
    }
  }

  prepareData = ( data = [] ) => data.map( interestType => {
    const prepared = InterestTypeView.calculateData( interestType )
    return {
      ...prepared,
      _onClick: () => this.props.history.push( "/manageInterestType/" + interestType.id ),
    }
  } )

  render () {
    const typeId = this.props.match.params.id
    if ( this.props.interestTypes.length === 0 ) {
      return <div/>
    }

    return (
      <div className={style.root}>
        {
          ( !typeId ) ?
            <BigList
              loadMore={() => {}}
              hasMoreItems={() => {}}
              items={this.prepareData( this.props.interestTypes )}
              columns={INTEREST_TYPE_COLUMNS}
            />
            :
            <InterestTypeView
              value={
                this.props.interestTypes.find( interestType => ( interestType.id + "" ) === typeId )
              }
              onChange={newInterestType => this.props.updateInterestType( newInterestType )}
            />
        }
      </div>
    )
  }
}


const mapStateToProps = store => {
  return {
    interestTypes: store.interestTypes,
  }
}

export default connect( mapStateToProps, {
  getAllInterestTypes,
  updateInterestType,
} )( withRouter( ManageInterestType ) )



class InterestTypeView extends Component {
  props: {
    value: mixed,
    onChange: ( mixed ) => any,
  }

  state = {
    addInterestTypeDialog: false,
    newTax: {
      value: undefined,
      startDate: null,
      endDate: null,
    },
    errors: {
      valueError: false,
      startDateError: false,
      endDateError: false,
    },
  }

  interestTypeRateColumns = [ {
    title: strings.generalStrings.value,
    key: "value",
    transform: val => formatPercent( val, 2 ),
  }, {
    title: strings.generalStrings.startDate,
    key: "startDate",
    transform: formatDate,
  }, {
    title: strings.generalStrings.endDate,
    key: "endDate",
    transform: formatDate,
  }, {
    key: "icon",
    transform: ( val, key, item ) => this.showIcon( val, key, item ),
  } ]

  static calculateData = interestType => {
    const rates = ( interestType.interestTypeRates || [] )
      .map( rate => ( {
        ...rate,
        startDate: new Date( rate.startDate ),
        endDate: rate.endDate !== null ? new Date( rate.endDate ) : null,
        value: parseFloat( rate.value ),
      } ) )

    const startPoint = _.minBy( rates, "startDate" )
    const endPoint = _.minBy( rates, "endDate" )
    const currentPoint = getCurrentInterestTax( rates )

    return {
      ...interestType,
      name: interestType.name,
      currentStartDate: currentPoint && formatDate( currentPoint.startDate ),
      currentEndDate: currentPoint && formatDate( currentPoint.endDate ),
      currentValue: currentPoint && formatPercent( currentPoint.value ),
      startDate: startPoint && formatDate( startPoint.startDate ),
      endDate: endPoint && formatDate( endPoint.endDate ),
    }
  }

  renderChart = ( sortedInteresTypeRate ) => {
    const data = ( sortedInteresTypeRate || [] )
      .map( dataPoint => {
        const startDate = new Date( dataPoint.startDate )
        const endDate = dataPoint.endDate === null ?
          new Date( startDate.setDate( startDate.getDate() + 10 ) ) :
          new Date( dataPoint.endDate )
        return {
          date: new Date( (
            endDate.getTime() +
          new Date( dataPoint.startDate ).getTime()
          ) / 2 ).getTime(),
          value: parseFloat( dataPoint.value ),
        } } )
      .reverse()
    return (
      <div className={style.chartWrapper}>
        <ResponsiveContainer width="99%" height="99%">
          <LineChart data={data}>
            <XAxis
              dataKey="date"
              name={strings.generalStrings.date}
              domain={[ "dataMin", "dataMax" ]}
              tickFormatter={formatDate}
              type="number"
            />
            <YAxis
              name={strings.generalStrings.value}
              tickFormatter={value => formatPercent( value, 0 )}
            />
            <CartesianGrid stroke="#eee" strokeDasharray="5 5"/>
            <Line
              type="step"
              dataKey="value"
              stroke="#03A9F4"
            />
            <Tooltip
              labelFormatter={timestamp => strings.generalStrings.formattedDate + formatDate( timestamp )}
              formatter={value => formatPercent( value )}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )
  }

  renderDetails = data => {
    return (
      <div className={style.detailsRoot}>
        <div className={style.detailsTitle}>{data.name}</div>

        <LabeledValue label={strings.generalStrings.startDate}>{data.startDate}</LabeledValue>
        <LabeledValue label={strings.generalStrings.endDate}>{data.endDate}</LabeledValue>
        <div/>

        <LabeledValue label={strings.generalStrings.currentValue}>{data.currentValue}</LabeledValue>
        <LabeledValue label={strings.management.startDatePeriod}>{data.currentStartDate}</LabeledValue>
        <LabeledValue label={strings.management.endDatePeriod}>{data.currentEndDate}</LabeledValue>
      </div>
    )
  }

  renderToolbar () {
    return (
      <div className={style.toolbar}>
        <div
          className={style.toolbarButton}
          onClick={this.handleToggleVisible}
          role="button"
          tabIndex="0"
        >
          <FontIcon value="add" /> {strings.management.addNewTax}
        </div>
      </div>
    )
  }

  handleToggleVisible = () => {
    return this.setState( {
      addInterestTypeDialog: !this.state.addInterestTypeDialog,
      newTax: {},
      errors: {},
    } )
  }

  checkInvalidDate = ( dateToCheck ) => {
    let isDateInvalid = false
    this.props.value.interestTypeRates.forEach( interestTypeRate => {
      const startDate = new Date( interestTypeRate.startDate )
      const endDate = new Date( interestTypeRate.endDate )
      if ( dateToCheck.getTime() <= endDate.getTime() && dateToCheck.getTime() >= startDate.getTime() ) {
        isDateInvalid = true
      }
    } )
    return isDateInvalid
  }

  handleUpdateInterestType = ( action, newInterestRate ) => {
    let newInterestType = Object.assign( {}, this.props.value )
    let newInterestTypeRates = _.cloneDeep( newInterestType.interestTypeRates )
    let sucessMessage = strings.management.taxAdded
    let errorMessage = strings.management.errorAddingTax

    if ( action === "add" ) {
      newInterestRate.value = newInterestRate.value.toFixed( 2 )
      newInterestRate.idInterestType = newInterestType.id
      if ( newInterestTypeRates.length !== 0 && newInterestTypeRates[ 0 ].endDate === null ) {
        newInterestTypeRates[ 0 ].endDate = new Date( newInterestRate.startDate ).setDate(
          new Date( newInterestRate.startDate ).getDate() - ONEDAY )
      }
      newInterestTypeRates.push( newInterestRate )
    } else {
      _.remove( newInterestTypeRates, rate => rate.id === newInterestRate.id )
      sucessMessage = "Taxa removida com sucesso"
      errorMessage = "Erro ao remover taxa"
    }

    newInterestType.interestTypeRates = newInterestTypeRates
    this.props.onChange( newInterestType ).then(
      () => toastr.success( sucessMessage ),
      () => toastr.error( errorMessage ),
    )
  }

  handleNewTax = () => {
    const { newTax } = this.state
    let addInterestTypeDialog = false

    const errorMessage = strings.errorMessages.missingField
    const errorInvalidValue = strings.errorMessages.invalidValue
    const errorInvalidDate = strings.errorMessages.invalidDate

    const valueError = !newTax.value ? errorMessage : newTax.value < 0 ? errorInvalidValue : false
    const startDateError = !newTax.startDate ? errorMessage :
      this.checkInvalidDate( new Date( newTax.startDate ) ) ? errorInvalidDate : false

    if ( !valueError && !startDateError ) {
      addInterestTypeDialog = true
      let newInterestRate = Object.assign( {}, this.state.newTax )
      this.handleUpdateInterestType( "add", newInterestRate )
    }

    this.setState( {
      errors: {
        valueError,
        startDateError,
      },
      addInterestTypeDialog: addInterestTypeDialog ? !this.state.addInterestTypeDialog : this.state.addInterestTypeDialog,
    } )
  }

  renderTypeDialog = ( lastDate, lastStartDate ) => {
    const { newTax } = this.state
    return (
      <Dialog
        className={style.dialogBox}
        actions={[
          { label: strings.generalStrings.cancel, onClick: this.handleToggleVisible },
          { label: strings.generalStrings.keep, onClick: this.handleNewTax },
        ]}
        active={this.state.addInterestTypeDialog}
        onEscKeyDown={this.handleToggleVisible}
        onOverlayClick={this.handleToggleVisible}
      >
        <div className={style.dialogContainer}>
          <div className={style.blueBar}>
          </div>
          <Division title={strings.management.addNewTax}/>
          <div className={[ style.addUserRow, style.dialogRow ].join( " " )}>
            <Input
              type="number"
              min="0"
              label={strings.management.interestTax}
              value={this.state.newTax.value}
              error={this.state.errors.valueError}
              onChange={( event ) => this.setState( {
                newTax: {
                  ...this.state.newTax,
                  value: parseFloat( event ),
                },
              } )}
            />
            <DatePicker
              label={strings.generalStrings.startDate}
              minDate={lastDate !== null ? lastDate : lastStartDate}
              value={newTax.startDate}
              error={this.state.errors.startDateError}
              onChange={( event ) => this.setState( {
                newTax: {
                  ...newTax,
                  startDate: event,
                },
              } )}
            />
            <DatePicker
              label={strings.generalStrings.endDate}
              value={newTax.endDate}
              error={this.state.errors.endDateError}
              minDate={newTax.startDate}
              onChange={( event ) => this.setState( {
                newTax: {
                  ...newTax,
                  endDate: event,
                },
              } )}
            />
          </div>
        </div>
      </Dialog>
    )
  }

  showIcon = ( value, key, item ) => {
    return <FontIcon onClick={() => this.handleUpdateInterestType( "remove", item )}
      style={{ cursor: "pointer" }}>{value}</FontIcon>
  }

  render () {
    if ( !this.props.value ) {
      return <div/>
    }
    const sortedInteresTypeRate = this.props.value.interestTypeRates
    sortedInteresTypeRate.sort( ( interestType, nextInterestType ) => {
      return new Date( nextInterestType.startDate ) - new Date( interestType.startDate )
    } )

    const lastDate = sortedInteresTypeRate.length !== 0 && sortedInteresTypeRate[ 0 ].endDate !== null ?
      new Date( sortedInteresTypeRate[ 0 ].endDate ) : null
    const lastStartDate = sortedInteresTypeRate.length !== 0 && sortedInteresTypeRate[ 0 ].startDate !== null ?
      new Date( sortedInteresTypeRate[ 0 ].startDate ) : null

    if ( sortedInteresTypeRate.length !== 0 ) {
      lastDate !== null ?
        lastDate.setDate( lastDate.getDate() + ONEDAY ) :
        lastStartDate.setDate( lastStartDate.getDate() + ONEDAY )
    }

    const data = InterestTypeView.calculateData( this.props.value )
    const newInterestTypeRates = this.props.value.interestTypeRates.map( interestRate => {
      return {
        ...interestRate,
        icon: "delete",
      }
    } )

    return (
      <div className={style.viewRoot}>
        <Card decor className={style.cardRoot}>
          {this.renderDetails( data )}
          <Spacer size={2}/>
          {this.renderChart( sortedInteresTypeRate )}
        </Card>
        <Spacer size={2}/>
        {this.renderToolbar()}
        {this.renderTypeDialog( lastDate, lastStartDate )}
        <div className={style.listWrapper}>
          <BigList
            loadMore={() => {}}
            hasMoreItems={() => {}}
            items={_.sortBy( newInterestTypeRates, "startDate" ).reverse()}
            columns={this.interestTypeRateColumns}
          />
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/views/ManageInterestType/index.jsx